import { useOrganizationId } from '@features/user';

import { useGetMissingRequirementsQuery } from './api';

export interface RequirementMissingCount {
  /** Patients whose requirement is still open. Undefined until the count lands. */
  count: number | undefined;
  isLoading: boolean;
  isError: boolean;
}

/**
 * How many patients are still missing ONE requirement (AC-23).
 *
 * The count is `total` off the missing list, asked for with the smallest page
 * the endpoint allows. There is no count field to read instead, and that is the
 * point: the number on the card is the same query AC-2's list pages through, so
 * the card and the list cannot disagree, and neither can drift from the rows
 * AC-21 materialized — they ARE those rows. A cached count is what the criterion
 * rules out.
 *
 * One definition per call: the endpoint takes one definition id, so a catalog of
 * N definitions costs N requests. Fine at today's handful; if the catalog grows
 * past that, the fix is a counts endpoint, not a client-side join.
 */
export const useRequirementMissingCount = (
  definitionId: string | undefined,
  { skip: skipRequested = false }: { skip?: boolean } = {},
): RequirementMissingCount => {
  const organizationId = useOrganizationId();
  const skip = skipRequested || !organizationId || !definitionId;

  const { data, isLoading, isFetching, isError } = useGetMissingRequirementsQuery(
    {
      orgId: organizationId ?? '',
      patientRequirementDefinitionId: definitionId ?? '',
      page: 1,
      perPage: 1,
    },
    { skip },
  );

  return {
    count: data?.total,
    // A revalidation over a number already on screen is not loading: a waive
    // invalidates this tag, and swapping the count for a skeleton on every
    // write would make the rail flicker while staff work down the list.
    isLoading: !skip && (isLoading || (isFetching && data === undefined)),
    isError,
  };
};
